'use client'

import { useEffect, useState } from 'react'
import slugify from 'slugify'
import { Collapsible, ExpandableContent as ExpandableContentSharp } from '@shc/ui'
import useCollapsibleAnalytics from '@hooks/use-collapsible-analytics'
import useUrlHash from '@hooks/use-url-hash'
import { richTextRenderFactory } from '@lib/rich-text-options'
import type {
  TypeExpandableContentFields,
  TypeExpandableContentItemFields,
} from '@lib/generated-types'

const ExpandableContent = ({
  items,
}: TypeExpandableContentFields) => {
  const richTextRenderChildren = richTextRenderFactory()
  const [handleCollapsibleAnalytics] = useCollapsibleAnalytics()
  const urlFragment = useUrlHash()
  const [isCollapsibleExpanded, setIsCollapsibleExpanded] = useState<any>({})

  useEffect(() => {
    const targetElem = document.getElementById(urlFragment)
    const className = targetElem?.className.split(' ')[1] as string

    /* By default, the element is aligned to the top (or bottom) edge of the scrollable ancestor.
       To define a custom spacing, use scroll-margin-top or scroll-margin-bottom.
       This is often useful when there's a fixed header on the page.
       Source: https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
    */
    if (targetElem?.id === urlFragment) {
      setIsCollapsibleExpanded((prevState: any) => ({
        ...prevState,
        [className]: true
      }))

      targetElem?.scrollIntoView({ behavior: 'smooth' })

      for (const item of items) {
        if (item.fields.sectionLink === urlFragment) {
          handleCollapsibleAnalytics(item.fields.title, item.fields, item.sys)
        }
      }
    }
  }, [urlFragment])

  return (
    <ExpandableContentSharp>
      {items.map(({ fields, sys }) => {
        const fieldsData = fields as TypeExpandableContentItemFields
        const id = fieldsData.sectionLink ? slugify(fieldsData.sectionLink) : undefined
        const sysId = sys.id

        return (
          <Collapsible
            className={`scroll-mt-16 ${sysId}`}
            description={
              <>{richTextRenderChildren(fieldsData.description)}</>
            }
            heading={fieldsData.title}
            id={id}
            key={sysId}
            onClick={() => handleCollapsibleAnalytics(fieldsData.title, fields, sys)}
            // Docs: https://www.radix-ui.com/primitives/docs/components/collapsible
            onOpenChange={() => {
              setIsCollapsibleExpanded((prevState: any) => ({
                ...prevState,
                [sysId]: !isCollapsibleExpanded[sysId]
              }))
            }}
            open={isCollapsibleExpanded[sysId]}
          />
      )})}
    </ExpandableContentSharp>
  )
}

export default ExpandableContent
